import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { clientPortalService } from '../../services/clientPortalService';
import { assessmentService } from '../../services/assessmentService';
import TimelineBrowser from './components/TimelineBrowser';
import ComparisonTools from './components/ComparisonTools';
import TrendAnalysis from './components/TrendAnalysis';
import DetailedScoreBreakdown from './components/DetailedScoreBreakdown';

const AssessmentHistoryAnalytics = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [progressData, setProgressData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAssessments, setSelectedAssessments] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState('all');

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }

    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assessmentsData, progressTrackingData] = await Promise.all([
        assessmentService?.getUserAssessments(user?.id),
        clientPortalService?.getClientProgressTracking(user?.id)
      ]);
      setAssessments(assessmentsData || []);
      setProgressData(progressTrackingData || []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleAssessmentSelect = (assessmentId) => {
    setSelectedAssessments(prev => {
      if (prev?.includes(assessmentId)) {
        return prev?.filter(id => id !== assessmentId);
      }
      if (prev?.length >= 3) {
        alert('You can compare up to 3 assessments at a time');
        return prev;
      }
      return [...prev, assessmentId];
    });
  };

  const handleClearSelection = () => {
    setSelectedAssessments([]);
  };

  const filteredAssessments = assessments?.filter(assessment => {
    if (filterType !== 'all' && assessment?.assessmentType !== filterType) {
      return false;
    }

    if (dateRange !== 'all') {
      const assessmentDate = new Date(assessment?.createdAt);
      const now = new Date();
      const monthsAgo = dateRange === '3months' ? 3 : dateRange === '6months' ? 6 : 12;
      const cutoffDate = new Date(now?.setMonth(now?.getMonth() - monthsAgo));
      if (assessmentDate < cutoffDate) {
        return false;
      }
    }

    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-[#1FB5A5] mx-auto" />
            <p className="mt-4 text-white">Loading assessment history...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto" />
            <p className="mt-4 text-red-400">{error}</p>
            <Button onClick={handleRefresh} className="mt-4">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Assessment History & Analytics | Northstar Data Strategy</title>
        <meta
          name="description"
          content="Comprehensive historical analysis of all assessments with advanced comparison and trend visualization."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />

        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="bg-gradient-to-r from-[#1FB5A5]/20 to-[#002B45]/20 backdrop-blur-sm rounded-xl p-8 border border-[#1FB5A5]/30">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-white mb-2">
                      Assessment History & Analytics
                    </h1>
                    <p className="text-gray-300 max-w-3xl">
                      Comprehensive historical analysis of all your assessments with advanced comparison tools and trend visualization capabilities. Track your data maturity journey across all five key dimensions.
                    </p>
                  </div>
                  <Button
                    onClick={handleRefresh}
                    variant="outline"
                    iconName="RefreshCw"
                    iconPosition="left"
                    className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  >
                    Refresh
                  </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <p className="text-gray-300 text-sm mb-1">Total Assessments</p>
                    <p className="text-3xl font-bold text-white">{assessments?.length}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <p className="text-gray-300 text-sm mb-1">Data Maturity</p>
                    <p className="text-3xl font-bold text-white">
                      {assessments?.filter(a => a?.assessmentType === 'data_maturity')?.length}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <p className="text-gray-300 text-sm mb-1">ROI Calculators</p>
                    <p className="text-3xl font-bold text-white">
                      {assessments?.filter(a => a?.assessmentType === 'roi_calculator')?.length}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <p className="text-gray-300 text-sm mb-1">Progress Entries</p>
                    <p className="text-3xl font-bold text-white">{progressData?.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="mb-6">
              <div className="bg-white rounded-xl shadow-elevation-1 p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Filter" size={18} className="text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Filters:</span>
                  </div>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e?.target?.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1FB5A5]"
                  >
                    <option value="all">All Types</option>
                    <option value="data_maturity">Data Maturity</option>
                    <option value="roi_calculator">ROI Calculator</option>
                  </select>
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e?.target?.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1FB5A5]"
                  >
                    <option value="all">All Time</option>
                    <option value="3months">Last 3 Months</option>
                    <option value="6months">Last 6 Months</option>
                    <option value="12months">Last 12 Months</option>
                  </select>
                  {selectedAssessments?.length > 0 && (
                    <div className="flex items-center gap-2 ml-auto">
                      <span className="text-sm text-gray-600">
                        {selectedAssessments?.length} selected for comparison
                      </span>
                      <Button
                        onClick={handleClearSelection}
                        variant="outline"
                        size="sm"
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Timeline Browser */}
            <TimelineBrowser
              assessments={filteredAssessments}
              selectedAssessments={selectedAssessments}
              onAssessmentSelect={handleAssessmentSelect}
            />

            {/* Comparison Tools */}
            {selectedAssessments?.length >= 2 && (
              <ComparisonTools
                assessments={assessments?.filter(a => selectedAssessments?.includes(a?.id))}
                progressData={progressData}
              />
            )}

            {/* Trend Analysis */}
            <TrendAnalysis
              progressData={progressData}
              assessments={assessments}
            />

            {/* Detailed Score Breakdown */}
            {selectedAssessments?.length === 1 && (
              <DetailedScoreBreakdown
                assessment={assessments?.find(a => a?.id === selectedAssessments?.[0])}
                progressData={progressData?.filter(p => p?.assessmentId === selectedAssessments?.[0])}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AssessmentHistoryAnalytics;